import { Button, Checkbox, Modal, Typography } from 'antd';
import { LockOutlined, RightOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectUser } from '@/slice/user';
import { UserRole } from '@/types';
import { ROUTES } from '@/types/routes';
import { useAcceptLegalDocuments } from '@/hooks/useLegalDocuments';
import './index.scss';

interface Props {
  open: boolean;
}

const formatDate = (iso?: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

// The most recent publish across the two documents — the date this update went
// live. Used for the "EFFECTIVE" stamp in the modal aside.
const latestDate = (a?: string | null, b?: string | null): string | null => {
  const times = [a, b]
    .filter((d): d is string => !!d)
    .map((d) => new Date(d).getTime());
  if (!times.length) return null;
  const max = Math.max(...times);
  return new Date(max).toISOString();
};

/**
 * TCX-668: blocking re-acceptance modal. Shown on the next login of any
 * consultant/franchisor whose accepted legal-document versions are behind the
 * current published ones. Cannot be dismissed — the only way forward is to
 * agree and accept (AC: users cannot bypass without accepting). The "Read …"
 * links open the public Privacy / Terms pages, which render the same published
 * versions being accepted here.
 */
export const ReacceptanceModal = ({ open }: Props) => {
  const user = useAppSelector(selectUser);
  const [agreed, setAgreed] = useState(false);
  const accept = useAcceptLegalDocuments();

  const legal = user?.legalReacceptance;
  const termsHref =
    user?.role === UserRole.FRANCHISEE
      ? ROUTES.TERMS_OF_USE_FRANCHISOR
      : ROUTES.TERMS_OF_USE_CONSULTANT;

  const effective = formatDate(
    latestDate(legal?.privacy?.publishedAt, legal?.terms?.publishedAt),
  );

  const handleAccept = () => {
    if (!agreed) return;
    // On success the mutation invalidates the profile query; withAuth refetches,
    // legalReacceptance.required flips false, and this modal unmounts.
    accept.mutate();
  };

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={680}
      className={'reacceptance-modal'}
      styles={{
        mask: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      <div className={'reacceptance'}>
        <div className={'reacceptance__aside'}>
          <span className={'reacceptance__lock'}>
            <LockOutlined />
          </span>
          <div className={'reacceptance__aside-meta'}>
            <span className={'reacceptance__effective-label'}>EFFECTIVE</span>
            {effective && (
              <span className={'reacceptance__effective-date'}>{effective}</span>
            )}
            {(legal?.privacy?.version != null ||
              legal?.terms?.version != null) && (
              <span className={'reacceptance__versions'}>
                Privacy v{legal?.privacy?.version ?? '—'} &middot; Terms v
                {legal?.terms?.version ?? '—'}
              </span>
            )}
          </div>
        </div>

        <div className={'reacceptance__body'}>
          <h2 className={'reacceptance__title'}>Please review our updates</h2>
          <p className={'reacceptance__subtitle'}>
            We&rsquo;ve refreshed our Privacy Policy and Terms of Use. Continued
            use of TCX requires your acceptance.
          </p>

          <a
            className={'reacceptance__link'}
            href={ROUTES.PRIVACY_AND_POLICY}
            target={'_blank'}
            rel={'noreferrer'}
          >
            <span>Read Privacy Policy</span>
            <RightOutlined />
          </a>
          <a
            className={'reacceptance__link'}
            href={termsHref}
            target={'_blank'}
            rel={'noreferrer'}
          >
            <span>Read Terms of Use</span>
            <RightOutlined />
          </a>

          <Checkbox
            className={'reacceptance__checkbox'}
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          >
            I agree to the updated Privacy Policy and Terms of Use.
          </Checkbox>

          {accept.isError && (
            <Typography.Text type={'danger'} className={'reacceptance__error'}>
              Something went wrong while saving your acceptance. Please try
              again.
            </Typography.Text>
          )}

          <Button
            type={'primary'}
            block
            disabled={!agreed}
            loading={accept.isPending}
            onClick={handleAccept}
            className={'reacceptance__cta'}
          >
            ACCEPT &amp; CONTINUE
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReacceptanceModal;
